import { IonIcon } from '@ionic/react';
import { lockClosedOutline, lockOpenOutline, notificationsOutline, warningOutline } from 'ionicons/icons';
import type { ReactNode } from 'react';
import RfidChip from './RfidChip';
import { getSoundLabel } from '../modals/edit-door-policy/EditDoorPolicySoundPicker';
import { useModalTranslation } from '../hooks/useTranslation';
import { parseTokenText } from '../utils/tokenText';
import styles from './TokenText.module.css';

interface TokenTextProps {
    text: string;
    /** Resolves cat tokens to the names this household uses. */
    deviceId?: string;
    /** Text is still arriving: an unterminated trailing token is hidden. */
    streaming?: boolean;
    className?: string;
}

/**
 * Renders assistant prose containing `[[kind:value]]` tokens as text with
 * inline chips, matching the rule-summary chip language elsewhere in the
 * door policy editor. Unknown kinds fall back to their plain value.
 */
const TokenText: React.FC<TokenTextProps> = ({ text, deviceId, streaming, className }) => {
    const { t: tModals } = useModalTranslation();
    const segments = parseTokenText(text, { streaming });

    const renderToken = (kind: string, value: string, key: number): ReactNode => {
        switch (kind) {
            case 'cat':
                return value ? <RfidChip key={key} compact deviceId={deviceId} rfidCode={value} /> : null;
            case 'time':
                return <span key={key} className={`${styles.token} ${styles.tokenTime}`}>{value}</span>;
            case 'lock':
                return (
                    <span key={key} className={`${styles.token} ${styles.tokenLock}`}>
                        <IonIcon icon={lockClosedOutline} aria-hidden="true" />
                        {value || tModals('door_policy.rule_summary.lock')}
                    </span>
                );
            case 'unlock':
                return (
                    <span key={key} className={`${styles.token} ${styles.tokenUnlock}`}>
                        <IonIcon icon={lockOpenOutline} aria-hidden="true" />
                        {value || tModals('door_policy.rule_summary.unlock')}
                    </span>
                );
            case 'sound':
                return (
                    <span key={key} className={`${styles.token} ${styles.tokenSound}`}>
                        <IonIcon icon={notificationsOutline} aria-hidden="true" />
                        {getSoundLabel(tModals, value)}
                    </span>
                );
            case 'flag':
                return (
                    <span key={key} className={`${styles.token} ${styles.tokenFlag}`}>
                        <IonIcon icon={warningOutline} aria-hidden="true" />
                        {value}
                    </span>
                );
            case 'where':
                return <span key={key} className={`${styles.token} ${styles.tokenWhere}`}>{value}</span>;
            default:
                return value ? <span key={key}>{value}</span> : null;
        }
    };

    return (
        <span className={className}>
            {segments.map((segment, index) =>
                segment.type === 'text' ? <span key={index}>{segment.text}</span> : renderToken(segment.kind, segment.value, index)
            )}
        </span>
    );
};

export default TokenText;
